/**
 * ReportarPantalla -- la ventana "Reportar lo que ves" (REPORTE-PANTALLAZO-T1, maqueta RcnJzrZAKLsfmnqpyCJh39, SI de
 * Josse #43980).
 *
 * AUDIENCIA: quien usa el panel (el dueno o el vendedor de Maderinco en el paso a paso, o el equipo en cualquier vista),
 * escritorio o celular. Se abre encima sin salir de la pantalla: toma el pantallazo de lo que se ve (con claves y datos
 * de tarjeta tapados), deja quitarlo, elegir que es (algo esta mal, una mejora, una duda) y escribir. Enviar guarda una
 * nota con la etiqueta 'reporte' (nexo-api 796 avisa a Josse por WhatsApp) y sube el pantallazo como adjunto.
 *
 * La captura (html2canvas 1.4.1, version fija) se carga solo al tocar Reportar. Reglas sin DOM en
 * application/reportes/reporte.js.
 *
 * @module presentation/components/ReportarPantalla
 */
import { NotesApiClient } from '../../infrastructure/api/NotesApiClient.js';
import { TIPOS, SENSIBLES, PANTALLAZO, pantallaDe, cuerpoDelReporte, idDeLaNota, fraseDelEnvio, medidaDelPantallazo, calidadSiguiente } from '../../application/reportes/reporte.js';
import { esc } from '../shared/texto.js';

/* global __APP_VERSION__ */
const VERSION = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '';
const ID = 'nx-reportar';

/** El pantallazo de lo que se ve ahora (sin la ventana), con lo sensible tapado; null si no se pudo. */
async function tomarPantallazo() {
  try {
    const { default: html2canvas } = await import('html2canvas');
    return await html2canvas(document.body, {
      x: window.scrollX, y: window.scrollY, width: window.innerWidth, height: window.innerHeight,
      windowWidth: document.documentElement.clientWidth, scale: Math.min(window.devicePixelRatio || 1, 1.5),
      backgroundColor: getComputedStyle(document.body).backgroundColor || '#0f1117', logging: false,
      ignoreElements: (el) => el.id === ID,
      onclone: (doc) => doc.querySelectorAll(SENSIBLES).forEach((el) => {
        el.value = ''; el.textContent = ''; el.style.background = '#30363d'; el.style.color = 'transparent';
      })
    });
  } catch (e) {
    console.warn('[Reportar] sin pantallazo:', e?.message || e);
    return null;
  }
}

const enBlob = (canvas, tipo, calidad) => new Promise((ok) => { try { canvas.toBlob((b) => ok(b), tipo, calidad); } catch { ok(null); } });

/**
 * MAD-REPORTAR-SIEMPRE-VISIBLE-T1 (Ares #44163): el pantallazo liviano para subir. Ancho maximo 1600 px y JPEG a 0,8;
 * si pasa de 300 KB, a 0,6. Devuelve el File o null.
 */
async function pantallazoLiviano(canvas) {
  const m = medidaDelPantallazo(canvas.width, canvas.height);
  let lienzo = canvas;
  if (m.ancho !== canvas.width) {
    lienzo = document.createElement('canvas');
    lienzo.width = m.ancho; lienzo.height = m.alto;
    const ctx = lienzo.getContext('2d');
    ctx.fillStyle = '#0f1117'; ctx.fillRect(0, 0, m.ancho, m.alto); // JPEG no tiene transparencia
    ctx.drawImage(canvas, 0, 0, m.ancho, m.alto);
  }
  let blob = null;
  for (let i = 0, q = calidadSiguiente(null, 0); q !== null; i++, q = calidadSiguiente(blob ? blob.size : null, i)) {
    blob = (await enBlob(lienzo, PANTALLAZO.tipo, q)) || blob;
  }
  return blob ? new File([blob], 'pantallazo.jpg', { type: PANTALLAZO.tipo }) : null;
}

/**
 * Abre la ventana. detalle = lo que precisa la pantalla ('Armar presupuesto › Cubierta'); el resto (vista, version)
 * lo pone el panel.
 */
export async function abrirReportar({ detalle = '', desde = null } = {}) {
  if (document.getElementById(ID)) return;
  // El foco vuelve al boton que abrio (en Safari o con el dedo el clic no le deja el foco).
  const vuelta = desde || document.activeElement;
  const pantalla = pantallaDe(window.location.hash, detalle);
  const canvas = await tomarPantallazo();
  const E = { tipo: 'bug', texto: '', conCaptura: !!canvas, enviando: false, error: '', listo: null };

  const $velo = document.createElement('div');
  $velo.id = ID;
  $velo.className = 'nx-rep-velo';
  document.body.appendChild($velo);
  const imagen = canvas ? canvas.toDataURL('image/png') : '';

  const cerrar = () => {
    $velo.remove();
    document.removeEventListener('keydown', alTeclear, true);
    // Si la pantalla se repinto mientras tanto, el boton es otro: se busca el Reportar que este.
    const destino = vuelta && document.contains(vuelta) ? vuelta : document.querySelector('[data-asi-reportar], #nx-fab-notes');
    if (destino && typeof destino.focus === 'function') destino.focus();
  };
  const alTeclear = (e) => {
    if (e.key === 'Escape' && !E.enviando) { e.preventDefault(); cerrar(); return; }
    if (e.key !== 'Tab') return;
    // El foco no sale de la ventana mientras esta abierta.
    const f = [...$velo.querySelectorAll('button:not([disabled]), textarea')];
    if (!f.length) return;
    if (e.shiftKey && document.activeElement === f[0]) { e.preventDefault(); f[f.length - 1].focus(); }
    else if (!e.shiftKey && document.activeElement === f[f.length - 1]) { e.preventDefault(); f[0].focus(); }
  };
  document.addEventListener('keydown', alTeclear, true);

  const pintar = (foco) => {
    if (E.listo) {
      $velo.innerHTML = `<div class="nx-rep-ventana" role="dialog" aria-modal="true" aria-labelledby="nx-rep-listo"><div class="nx-rep-listo">
        <b id="nx-rep-listo">Enviado a Nexo</b><p>${esc(E.listo)}</p>
        <button type="button" class="nx-rep-enviar" data-rep-cerrar>Seguir</button></div></div>`;
    } else {
      $velo.innerHTML = `<div class="nx-rep-ventana" role="dialog" aria-modal="true" aria-labelledby="nx-rep-titulo">
        <div class="nx-rep-cabeza"><h2 id="nx-rep-titulo">Reportar lo que ves</h2><button type="button" class="nx-rep-cerrar" data-rep-cerrar aria-label="Cerrar sin enviar">&times;</button></div>
        ${E.conCaptura
          ? `<figure class="nx-rep-captura"><img src="${imagen}" alt="Pantallazo de esta pantalla"><figcaption><span>Pantallazo de esta pantalla · se guarda en Notas de Nexo · claves, cliente y proyecto salen tapados</span><button type="button" class="nx-rep-boton" data-rep-quitar>Quitar el pantallazo</button></figcaption></figure>`
          : `<div class="nx-rep-sin"><span>${canvas ? 'Sin pantallazo.' : 'No se pudo tomar el pantallazo: se envía sin él.'}</span>${canvas ? '<button type="button" class="nx-rep-boton" data-rep-poner>Volver a ponerlo</button>' : ''}</div>`}
        <div class="nx-rep-tipo"><span id="nx-rep-tipo">Qué es</span><div class="nx-rep-chips" role="group" aria-labelledby="nx-rep-tipo">${TIPOS.map((t) => `<button type="button" class="nx-rep-chip" data-rep-tipo="${t.id}" aria-pressed="${E.tipo === t.id}">${t.texto}</button>`).join('')}</div></div>
        <div class="nx-rep-texto"><label for="nx-rep-texto">Qué pasó o qué quieres</label><textarea id="nx-rep-texto" rows="4" data-rep-texto>${esc(E.texto)}</textarea></div>
        <p class="nx-rep-donde">Se envía con la pantalla en la que estás (${esc(pantalla)}) y la versión del panel.</p>
        ${E.error ? `<p class="nx-rep-error" role="alert">${esc(E.error)}</p>` : ''}
        <div class="nx-rep-acciones"><button type="button" class="nx-rep-enviar" data-rep-enviar${E.texto.trim() && !E.enviando ? '' : ' disabled'}>${E.enviando ? 'Enviando…' : 'Enviar a Nexo'}</button><button type="button" class="nx-rep-boton" data-rep-cerrar${E.enviando ? ' disabled' : ''}>Cancelar</button></div>
      </div>`;
    }
    $velo.querySelector(foco || '[data-rep-texto]')?.focus();
  };

  $velo.addEventListener('input', (e) => {
    if (!e.target.matches('[data-rep-texto]')) return;
    E.texto = e.target.value;
    const $e = $velo.querySelector('[data-rep-enviar]');
    if ($e) $e.disabled = !E.texto.trim() || E.enviando;
  });
  $velo.addEventListener('click', async (e) => {
    if (e.target === $velo && !E.enviando) { cerrar(); return; }
    const b = e.target.closest('button');
    if (!b || b.disabled) return;
    if (b.hasAttribute('data-rep-cerrar')) { cerrar(); return; }
    if (b.hasAttribute('data-rep-quitar')) { E.conCaptura = false; pintar('[data-rep-poner]'); return; }
    if (b.hasAttribute('data-rep-poner')) { E.conCaptura = true; pintar('[data-rep-quitar]'); return; }
    if (b.dataset.repTipo) { E.tipo = b.dataset.repTipo; pintar(`[data-rep-tipo="${E.tipo}"]`); return; }
    if (!b.hasAttribute('data-rep-enviar')) return;
    let negocio = '';
    try { negocio = localStorage.getItem('nx_selected_business') || ''; } catch { /* sin almacenamiento */ }
    const v = cuerpoDelReporte({ tipo: E.tipo, texto: E.texto, pantalla, version: VERSION, ruta: window.location.hash, negocio });
    if (!v.ok) return;
    E.enviando = true; E.error = ''; pintar('[data-rep-texto]');
    const nota = await NotesApiClient.create(v.cuerpo);
    const id = nota.ok ? idDeLaNota(nota) : null;
    let adjunto = null;
    if (id && E.conCaptura && canvas) {
      const archivo = await pantallazoLiviano(canvas);
      adjunto = archivo ? await NotesApiClient.uploadAttachment(id, archivo) : null;
    }
    const r = fraseDelEnvio({ nota, adjunto, conPantallazo: E.conCaptura && !!canvas });
    E.enviando = false;
    if (!r.ok) { E.error = r.frase; pintar('[data-rep-enviar]'); return; }
    E.listo = r.frase; pintar('[data-rep-cerrar]');
  });
  pintar();
}
